import React from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, RefreshCw } from 'lucide-react';
import { LOGO_ICON_BLUE } from '@/components/SplashScreen';

export default function RefundPolicy() {
  const navigate = useNavigate();

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <header className="bg-white shadow-sm sticky top-0 z-10">
        <div className="max-w-4xl mx-auto px-6 py-4 flex items-center gap-4">
          <button
            onClick={() => navigate('/')}
            className="p-2 hover:bg-gray-100 rounded-full transition-colors"
          >
            <ArrowLeft className="h-5 w-5" />
          </button>
          <img src={LOGO_ICON_BLUE} alt="Rebuilt" className="h-8 w-8" />
          <h1 className="text-xl font-bold text-gray-900">Refund & Cancellation Policy</h1>
        </div>
      </header>

      {/* Content */}
      <div className="max-w-4xl mx-auto px-6 py-12">
        <div className="bg-white rounded-2xl shadow-sm p-8 md:p-12">
          <div className="flex items-center gap-3 mb-6">
            <RefreshCw className="h-8 w-8 text-blue-600" />
            <h2 className="text-3xl font-bold text-gray-900">Refund & Cancellation Policy</h2>
          </div>

          <p className="text-sm text-gray-500 mb-8">
            <strong>Last Updated:</strong> January 1, 2025
          </p>

          <div className="prose prose-gray max-w-none space-y-8">
            {/* Introduction */}
            <section>
              <h3 className="text-xl font-semibold text-gray-900 mb-4">1. Introduction</h3>
              <p className="text-gray-700 leading-relaxed">
                This Refund and Cancellation Policy outlines the terms and conditions for loan application cancellations and refunds on Rebuilt's EMI assistance platform. Please read this policy carefully before using our services.
              </p>
            </section>

            {/* Loan Application Cancellation */}
            <section>
              <h3 className="text-xl font-semibold text-gray-900 mb-4">2. Loan Application Cancellation</h3>
              
              <p className="text-gray-700 font-semibold mb-3">2.1 Before Disbursement</p>
              <p className="text-gray-700 leading-relaxed mb-3">
                You may cancel your loan application at any time before disbursement without penalty. To cancel:
              </p>
              <ul className="list-disc pl-6 space-y-2 text-gray-700 mb-4">
                <li>Log in to your account</li>
                <li>Navigate to 'My Applications'</li>
                <li>Select the application and click 'Cancel'</li>
                <li>Or contact our support team at support@rebuilt.in</li>
              </ul>

              <p className="text-gray-700 font-semibold mb-3">2.2 After Disbursement</p>
              <p className="text-gray-700 leading-relaxed mb-3">
                Once the loan amount has been disbursed to your bank account, the loan cannot be cancelled. You are obligated to repay the full amount as per the agreed terms.
              </p>

              <p className="text-gray-700 font-semibold mb-3">2.3 Cooling-Off Period</p>
              <p className="text-gray-700 leading-relaxed">
                You have a 24-hour cooling-off period after loan approval but before disbursement. During this period, you may cancel without any charges.
              </p>
            </section>

            {/* Processing Fee Refund */}
            <section>
              <h3 className="text-xl font-semibold text-gray-900 mb-4">3. Processing Fee Refund Policy</h3>
              
              <p className="text-gray-700 font-semibold mb-3">3.1 Non-Refundable Processing Fees</p>
              <p className="text-gray-700 leading-relaxed mb-3">
                Processing fees charged for loan application processing are generally non-refundable once payment is made, as they cover:
              </p>
              <ul className="list-disc pl-6 space-y-2 text-gray-700 mb-4">
                <li>KYC verification costs</li>
                <li>Credit bureau report charges</li>
                <li>Document verification expenses</li>
                <li>Underwriting and risk assessment costs</li>
              </ul>

              <p className="text-gray-700 font-semibold mb-3">3.2 Exceptions for Refund</p>
              <p className="text-gray-700 leading-relaxed mb-3">
                Processing fees may be refunded in the following exceptional circumstances:
              </p>
              <ul className="list-disc pl-6 space-y-2 text-gray-700">
                <li><strong>Technical Error:</strong> If you were charged multiple times due to a technical glitch</li>
                <li><strong>Duplicate Payment:</strong> If the same processing fee was debited more than once</li>
                <li><strong>Service Failure:</strong> If we failed to process your application due to our error</li>
                <li><strong>Unauthorized Transaction:</strong> If the payment was made without your authorization</li>
              </ul>
            </section>

            {/* Refund Process */}
            <section>
              <h3 className="text-xl font-semibold text-gray-900 mb-4">4. Refund Process and Timeline</h3>
              
              <p className="text-gray-700 font-semibold mb-3">4.1 Initiating a Refund Request</p>
              <p className="text-gray-700 leading-relaxed mb-3">
                To request a refund:
              </p>
              <ol className="list-decimal pl-6 space-y-2 text-gray-700 mb-4">
                <li>Email refunds@rebuilt.in with your transaction details</li>
                <li>Include: Transaction ID, Date, Amount, Reason for refund</li>
                <li>Attach supporting documents if applicable</li>
                <li>Our team will review within 3-5 business days</li>
              </ol>

              <p className="text-gray-700 font-semibold mb-3">4.2 Refund Timeline</p>
              <ul className="list-disc pl-6 space-y-2 text-gray-700">
                <li><strong>Approval:</strong> Refund requests are reviewed within 3-5 business days</li>
                <li><strong>Processing:</strong> Approved refunds are initiated within 7 business days</li>
                <li><strong>Credit to Account:</strong> Refunds reflect in your account within 5-10 business days depending on your bank</li>
                <li><strong>Mode:</strong> Refunds are credited to the original payment method used</li>
              </ul>
            </section>

            {/* Early Repayment */}
            <section>
              <h3 className="text-xl font-semibold text-gray-900 mb-4">5. Early Repayment and Foreclosure</h3>
              
              <p className="text-gray-700 font-semibold mb-3">5.1 Prepayment Option</p>
              <p className="text-gray-700 leading-relaxed mb-3">
                You may repay your loan in full before the scheduled maturity date. Early repayment benefits include:
              </p>
              <ul className="list-disc pl-6 space-y-2 text-gray-700 mb-4">
                <li>Reduced total interest payment</li>
                <li>Improved credit score</li>
                <li>Freedom from debt obligation</li>
              </ul>

              <p className="text-gray-700 font-semibold mb-3">5.2 No Prepayment Penalty</p>
              <p className="text-gray-700 leading-relaxed mb-3">
                Rebuilt does not charge any prepayment penalty or foreclosure charges. You only pay:
              </p>
              <ul className="list-disc pl-6 space-y-2 text-gray-700">
                <li>Outstanding principal amount</li>
                <li>Interest accrued up to the date of repayment</li>
                <li>Any pending late fees or charges (if applicable)</li>
              </ul>

              <p className="text-gray-700 font-semibold mb-3 mt-4">5.3 Refund of Excess Interest</p>
              <p className="text-gray-700 leading-relaxed">
                If you repay early, interest charged for the remaining period will not be applied. You will only pay interest for the actual number of days the loan was outstanding.
              </p>
            </section>

            {/* Payment Gateway Failures */}
            <section>
              <h3 className="text-xl font-semibold text-gray-900 mb-4">6. Payment Gateway Failures</h3>
              
              <p className="text-gray-700 font-semibold mb-3">6.1 Failed Transactions</p>
              <p className="text-gray-700 leading-relaxed mb-3">
                If money is debited from your account but the transaction fails or times out:
              </p>
              <ul className="list-disc pl-6 space-y-2 text-gray-700 mb-4">
                <li>The amount will be automatically refunded by the payment gateway within 5-7 business days</li>
                <li>If not received within 7 days, contact support@rebuilt.in</li>
                <li>Provide transaction screenshot and bank statement</li>
              </ul>

              <p className="text-gray-700 font-semibold mb-3">6.2 Double Debit</p>
              <p className="text-gray-700 leading-relaxed">
                If your account is debited twice for the same transaction, the duplicate amount will be refunded within 7-10 business days after verification.
              </p>
            </section>

            {/* Disbursement Reversal */}
            <section>
              <h3 className="text-xl font-semibold text-gray-900 mb-4">7. Disbursement Reversal</h3>
              
              <p className="text-gray-700 font-semibold mb-3">7.1 Wrong Account Disbursement</p>
              <p className="text-gray-700 leading-relaxed mb-3">
                If loan amount is disbursed to a wrong account due to incorrect details provided by you:
              </p>
              <ul className="list-disc pl-6 space-y-2 text-gray-700 mb-4">
                <li>Rebuilt is not responsible for recovery</li>
                <li>You remain liable to repay the full loan amount</li>
                <li>We will assist in recovery efforts but cannot guarantee success</li>
              </ul>

              <p className="text-gray-700 font-semibold mb-3">7.2 Our Error in Disbursement</p>
              <p className="text-gray-700 leading-relaxed">
                If disbursement error occurs due to our mistake, we will reverse the transaction and re-disburse to the correct account within 3-5 business days at no additional cost.
              </p>
            </section>

            {/* Exceptions */}
            <section>
              <h3 className="text-xl font-semibold text-gray-900 mb-4">8. Non-Refundable Items</h3>
              <p className="text-gray-700 leading-relaxed mb-3">
                The following are non-refundable under any circumstances:
              </p>
              <ul className="list-disc pl-6 space-y-2 text-gray-700">
                <li>Processing fees (except in cases mentioned in Section 3.2)</li>
                <li>Late payment charges</li>
                <li>Penalty fees for dishonored payments</li>
                <li>Third-party charges (payment gateway, bank charges)</li>
                <li>Interest on disbursed loan amounts</li>
              </ul>
            </section>

            {/* Dispute Resolution */}
            <section>
              <h3 className="text-xl font-semibold text-gray-900 mb-4">9. Dispute Resolution</h3>
              <p className="text-gray-700 leading-relaxed mb-3">
                If you disagree with our refund decision:
              </p>
              <ol className="list-decimal pl-6 space-y-2 text-gray-700">
                <li>Email your detailed concern to grievance@rebuilt.in</li>
                <li>Our Grievance Officer will review within 5 business days</li>
                <li>You will receive a detailed response with resolution or explanation</li>
                <li>If still unsatisfied, you may escalate to our senior management</li>
              </ol>
            </section>

            {/* Policy Changes */}
            <section>
              <h3 className="text-xl font-semibold text-gray-900 mb-4">10. Changes to This Policy</h3>
              <p className="text-gray-700 leading-relaxed">
                We reserve the right to modify this Refund and Cancellation Policy at any time. Changes will be posted on our platform with an updated "Last Updated" date. Continued use of our services constitutes acceptance of the revised policy.
              </p>
            </section>

            {/* Contact */}
            <section>
              <h3 className="text-xl font-semibold text-gray-900 mb-4">11. Contact Information</h3>
              <p className="text-gray-700 leading-relaxed mb-3">
                For refund and cancellation queries:
              </p>
              <div className="bg-blue-50 p-4 rounded-lg">
                <p className="text-gray-800"><strong>Email:</strong> refunds@rebuilt.in</p>
                <p className="text-gray-800"><strong>Support Email:</strong> support@rebuilt.in</p>
                <p className="text-gray-800"><strong>Phone:</strong> +91-XXXX-XXXXXX</p>
                <p className="text-gray-800"><strong>Working Hours:</strong> Monday to Saturday, 10:00 AM - 6:00 PM IST</p>
              </div>
            </section>
          </div>

          {/* Back to Home Button */}
          <div className="mt-12 pt-8 border-t border-gray-200">
            <button
              onClick={() => navigate('/')}
              className="w-full sm:w-auto px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors font-medium"
            >
              Back to Home
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
